import { AlertTriangle, CreditCard, Landmark } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/hooks/useI18n';
import { cn } from '@/components/utils/utils';
import type { TranslationKey } from '@/types/i18n.types';
import {
    IBankTransferDetails,
    IPaymentOptionsForm,
    PaymentOptionsFieldError,
    ProposalPaymentMethod,
} from '../models/payment-options.model';

interface PaymentOptionsFormProps {
    value: IPaymentOptionsForm;
    onChange: (value: IPaymentOptionsForm) => void;
    errors?: Set<PaymentOptionsFieldError>;
    /** Chi incassa ha Stripe attivo: altrimenti l'opzione resta selezionabile ma avvisa. */
    stripeActive: boolean;
    /** Coordinate predefinite di chi incassa, per il tasto "usa le predefinite". */
    defaults?: Partial<IBankTransferDetails> | null;
}

type BankField = keyof IBankTransferDetails;

/**
 * Scelta dei metodi di pagamento di una proposta e coordinate del bonifico.
 * Stato controllato dal genitore: send-quote e la modale pagamenti salvano in momenti
 * diversi, il form non sa quando.
 */
export const PaymentOptionsForm = ({
    value,
    onChange,
    errors = new Set(),
    stripeActive,
    defaults,
}: PaymentOptionsFormProps) => {
    const { t } = useI18n();

    const bankSelected = value.methods.includes('bank_transfer');
    const hasDefaults = !!(defaults?.bank_account_holder && defaults?.bank_iban);

    const toggleMethod = (method: ProposalPaymentMethod, checked: boolean) => {
        const methods = checked
            ? [...value.methods.filter((m) => m !== method), method]
            : value.methods.filter((m) => m !== method);
        onChange({ ...value, methods });
    };

    const setField = (field: BankField, fieldValue: string) => onChange({ ...value, [field]: fieldValue });

    const applyDefaults = () => {
        if (!defaults) return;
        const next = { ...value };
        (Object.keys(defaults) as BankField[]).forEach((k) => {
            if (k in next) next[k] = defaults[k] ?? '';
        });
        onChange(next);
    };

    const methodOption = (
        method: ProposalPaymentMethod,
        Icon: typeof CreditCard,
        warning?: string | null,
    ) => {
        const checked = value.methods.includes(method);
        return (
            <label
                htmlFor={`payment-method-${method}`}
                className={cn(
                    'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                    checked ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50',
                    errors.has('methods') && 'border-yellow-400',
                )}
            >
                <Checkbox
                    id={`payment-method-${method}`}
                    checked={checked}
                    onCheckedChange={(c) => toggleMethod(method, c === true)}
                    className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                        <Icon className="h-4 w-4" />
                        {t(`paymentOptions.methods.${method}`)}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{t(`paymentOptions.methods.${method}Desc`)}</p>
                    {checked && warning && (
                        <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3 shrink-0" />
                            {warning}
                        </p>
                    )}
                </div>
            </label>
        );
    };

    const textField = (field: BankField, labelKey: TranslationKey, optional = false, errorKey?: TranslationKey) => (
        <div className="space-y-1">
            <Label htmlFor={`bank-${field}`} className="text-xs">
                {t(labelKey)}
                {optional && <span className="text-gray-400 font-normal"> ({t('paymentOptions.bank.optional')})</span>}
            </Label>
            <Input
                id={`bank-${field}`}
                value={value[field]}
                onChange={(e) => setField(field, e.target.value)}
                hasError={errors.has(field as PaymentOptionsFieldError)}
                className="h-9"
            />
            {errors.has(field as PaymentOptionsFieldError) && errorKey && (
                <p className="text-xs text-yellow-700">{t(errorKey)}</p>
            )}
        </div>
    );

    return (
        <div className="space-y-4">
            <div className="space-y-2">
                <p className="text-sm text-gray-500">{t('paymentOptions.subtitle')}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {methodOption('stripe', CreditCard, stripeActive ? null : t('paymentOptions.stripeInactive'))}
                    {methodOption('bank_transfer', Landmark, t('paymentOptions.feeNotice'))}
                </div>
                {errors.has('methods') && <p className="text-xs text-yellow-700">{t('paymentOptions.required')}</p>}
            </div>

            {bankSelected && (
                <div className="rounded-lg border border-gray-200 p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-gray-900">{t('paymentOptions.bank.title')}</span>
                        {hasDefaults && (
                            <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={applyDefaults}>
                                {t('paymentOptions.bank.useDefaults')}
                            </Button>
                        )}
                    </div>
                    {!hasDefaults && <p className="text-xs text-gray-500">{t('paymentOptions.bank.noDefaults')}</p>}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {textField('bank_account_holder', 'paymentOptions.bank.accountHolder', false, 'paymentOptions.bank.requiredField')}
                        {textField('bank_iban', 'paymentOptions.bank.iban', false, 'paymentOptions.bank.invalidIban')}
                        {textField('bank_bic', 'paymentOptions.bank.bic', true)}
                        {textField('bank_name', 'paymentOptions.bank.bankName', true)}
                    </div>
                    {textField('bank_reference', 'paymentOptions.bank.reference', true)}

                    <div className="space-y-1">
                        <Label htmlFor="bank-instructions" className="text-xs">
                            {t('paymentOptions.bank.instructions')}
                            <span className="text-gray-400 font-normal"> ({t('paymentOptions.bank.optional')})</span>
                        </Label>
                        <Textarea
                            id="bank-instructions"
                            rows={2}
                            value={value.bank_instructions}
                            placeholder={t('paymentOptions.bank.instructionsPlaceholder')}
                            onChange={(e) => setField('bank_instructions', e.target.value)}
                        />
                    </div>

                    <div className="space-y-1">
                        <Label htmlFor="bank-receipt-note" className="text-xs">{t('paymentOptions.bank.receiptNote')}</Label>
                        <Textarea
                            id="bank-receipt-note"
                            rows={3}
                            value={value.bank_receipt_note || t('paymentOptions.bank.defaultReceiptNote')}
                            onChange={(e) => setField('bank_receipt_note', e.target.value)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
